/**
 * Form Data Transformers
 * Utility functions for transforming ReactSelect objects and form data
 * Used across NCR, IPI, and Customer Complaint forms
 */

/**
 * Extract value and label from ReactSelect object, returning normalized {value, label} format
 * @param {Object|string|number|null} field - ReactSelect object or primitive value
 * @returns {Object|null} - Normalized {value, label} object or null
 */
export const extractSelectValue = (field) => {
  if (!field) return null;
  
  if (typeof field === 'object' && !Array.isArray(field)) {
    return {
      value: field.value || field,
      label: field.label || field.value || field
    };
  }
  
  // Handle primitive values
  return {
    value: field,
    label: field
  };
};

/**
 * Format a date string or Date object to ISO 8601 (YYYY-MM-DD) for Fusion Manage.
 * Returns an empty string when the input is null/empty and leaves unknown formats untouched.
 * @param {string|Date|null} dateValue
 * @returns {string}
 */
export const formatDateForFusion = (dateValue) => {
  if (!dateValue) return '';

  if (dateValue instanceof Date && !Number.isNaN(dateValue.getTime())) {
    return dateValue.toISOString().split('T')[0];
  }

  const trimmed = String(dateValue).trim();
  if (!trimmed) return '';

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return trimmed;
  }

  const localizedMatch = trimmed.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);
  if (localizedMatch) {
    let [, month, day, year] = localizedMatch;
    if (year.length === 2) {
      const numericYear = parseInt(year, 10);
      year = numericYear >= 70 ? `19${year}` : `20${year}`;
    }
    const paddedMonth = month.padStart(2, '0');
    const paddedDay = day.padStart(2, '0');
    const paddedYear = year.padStart(4, '0');
    return `${paddedYear}-${paddedMonth}-${paddedDay}`;
  }

  const parsedDate = new Date(trimmed);
  if (!Number.isNaN(parsedDate.getTime())) {
    return parsedDate.toISOString().split('T')[0];
  }

  return trimmed;
};

/**
 * Extract label from ReactSelect object or return the value itself
 * @param {Object|string|number|null} field - ReactSelect object or primitive value
 * @returns {string|null} - Label string or null
 */
export const extractSelectLabel = (field) => {
  if (!field) return null;
  
  if (typeof field === 'object' && !Array.isArray(field)) {
    return field.label || field.value || null;
  }
  
  return field;
};

/**
 * Normalize ReactSelect field to {value, label} format
 * Handles both object and primitive values
 * @param {Object|string|number|null} field - ReactSelect object or primitive value
 * @returns {Object|null} - Normalized {value, label} object or null
 */
export const normalizeSelectField = (field) => {
  if (!field) return null;
  
  if (typeof field === 'object' && !Array.isArray(field)) {
    return {
      value: field.value || field,
      label: field.label || field.value || field
    };
  }
  
  return {
    value: field,
    label: field
  };
};

/**
 * Prepare location fields (GlobalSite, Site, Department) for submission
 * Extracts value and label from ReactSelect objects
 * @param {Object} formData - Form data object
 * @param {string} prefix - Field prefix ('Assigned' or 'RaisedBy')
 * @returns {Object} - Object with normalized location fields
 */
export const prepareLocationFields = (formData, prefix = 'Assigned') => {
  const globalSiteKey = prefix === 'RaisedBy' ? 'GlobalSiteRaisedBy' : 'GlobalSiteAssigned';
  const siteKey = prefix === 'RaisedBy' ? 'SiteRaisedBy' : 'SiteAssigned';
  const deptKey = prefix === 'RaisedBy' ? 'DepartmentRaisedBy' : 'DepartmentAssigned';
  
  return {
    [globalSiteKey]: normalizeSelectField(formData[globalSiteKey]),
    [siteKey]: normalizeSelectField(formData[siteKey]),
    [deptKey]: normalizeSelectField(formData[deptKey])
  };
};

/**
 * Prepare RelatedSupplier field for submission
 * Handles object format, array format, and primitive values
 * @param {Object|Array|string|number|null} field - RelatedSupplier field value
 * @param {string} formName - Name of the form (for logging)
 * @returns {Object|null} - Normalized {value, label} object or null
 */
export const prepareRelatedSupplier = (field, formName = 'Form') => {
  if (!field) {
    console.log(`[${formName}] RelatedSupplier is empty/null, not including in submission`);
    return null;
  }
  
  // Handle object format (standard case for single select)
  if (typeof field === 'object' && !Array.isArray(field)) {
    const result = {
      value: field.value || field,
      label: field.label || field.value || field
    };
    console.log(`[${formName}] RelatedSupplier being sent:`, result);
    return result;
  }
  
  // Handle string/number format (fallback for backward compatibility)
  if (field) {
    console.log(`[${formName}] RelatedSupplier (primitive format) being sent:`, field);
    return {
      value: field,
      label: field
    };
  }
  
  return null;
};

/**
 * Prepare InternalOrExternal field for submission
 * Normalizes to {value, label} format
 * @param {Object|string|null} field - InternalOrExternal field value
 * @returns {Object|null} - Normalized {value, label} object or null
 */
export const prepareInternalOrExternal = (field) => {
  if (!field) return null;
  
  if (typeof field === 'object' && field !== null) {
    return {
      value: field.value || field.label || field,
      label: field.label || field.value || field
    };
  }
  
  return {
    value: field,
    label: field
  };
};

/**
 * Extract ProductName label (extracted product name after dash)
 * @param {Object|string|null} field - ProductName field value
 * @returns {string|null} - Extracted label or null
 */
export const extractProductName = (field) => {
  if (!field) return null;
  
  if (typeof field === 'object') {
    return field.label || field.value || null;
  }
  
  // If it's a string, it should already be the extracted text
  return field;
};

/**
 * Prepare cost fields, converting strings to numbers
 * @param {Object} formData - Form data object
 * @returns {Object} - Object with normalized cost fields
 */
export const prepareCostFields = (formData) => {
  return {
    CostOfNCR: formData.CostOfNCR ? parseFloat(formData.CostOfNCR) : null,
    ShippingCost: formData.ShippingCost ? parseFloat(formData.ShippingCost) : null,
    MaterialCost: formData.MaterialCost ? parseFloat(formData.MaterialCost) : null,
    LaborCost: formData.LaborCost ? parseFloat(formData.LaborCost) : null
  };
};

/**
 * Prepare all assignment fields (Assigned and RaisedBy) for submission
 * @param {Object} formData - Form data object
 * @returns {Object} - Object with all normalized assignment fields
 */
export const prepareAllAssignmentFields = (formData) => {
  const assigned = prepareLocationFields(formData, 'Assigned');
  const raisedBy = prepareLocationFields(formData, 'RaisedBy');
  
  return {
    ...assigned,
    ...raisedBy,
    NCRChampionAssigned: normalizeSelectField(formData.NCRChampionAssigned),
    NCRCapabilityAssigned: normalizeSelectField(formData.NCRCapabilityAssigned),
    QAEmail: formData.QAEmail?.value || formData.QAEmail?.label || formData.QAEmail,
    RaisedBy: formData.RaisedBy,
    RaisedDate: formatDateForFusion(formData.RaisedDate)
  };
};

